import React, { useState } from 'react';
import type { ServiceSuggestion } from '@om/shared-types';
import { ReasonTooltip } from './ReasonTooltip';

interface ServiceCheckboxItemProps {
  service: ServiceSuggestion;
  onChange: (id: number, selected: boolean, priority: 'must_have' | 'optional') => void;
}

export function ServiceCheckboxItem({ service, onChange }: ServiceCheckboxItemProps) {
  const [selected, setSelected] = useState(service.is_selected);
  const [priority, setPriority] = useState<'must_have' | 'optional'>(
    service.priority ?? 'optional'
  );

  function handleCheckbox(e: React.ChangeEvent<HTMLInputElement>) {
    const checked = e.target.checked;
    setSelected(checked);
    onChange(service.id, checked, priority);
  }

  function handlePriority(p: 'must_have' | 'optional') {
    setPriority(p);
    if (selected) onChange(service.id, selected, p);
  }

  return (
    <div className="flex items-start gap-3 rounded-md px-2 py-2 hover:bg-gray-50">
      <input
        type="checkbox"
        id={`svc-${service.id}`}
        checked={selected}
        onChange={handleCheckbox}
        className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
      />
      <label htmlFor={`svc-${service.id}`} className="flex-1 cursor-pointer">
        <span className="text-sm font-medium text-gray-800">{service.name}</span>
        {service.description && (
          <span className="ml-1 text-xs text-gray-500">— {service.description}</span>
        )}
        {service.typical_price_range && (
          <span className="ml-2 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 capitalize">
            {service.typical_price_range}
          </span>
        )}
      </label>
      {service.suggestion_reason && (
        <ReasonTooltip reason={service.suggestion_reason} />
      )}
      {selected && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handlePriority('must_have')}
            className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
              priority === 'must_have'
                ? 'bg-rose-100 text-rose-700'
                : 'bg-gray-100 text-gray-500 hover:bg-rose-50 hover:text-rose-600'
            }`}
          >
            Must have
          </button>
          <button
            type="button"
            onClick={() => handlePriority('optional')}
            className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
              priority === 'optional'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600'
            }`}
          >
            Optional
          </button>
        </div>
      )}
    </div>
  );
}
